/* Shared product option presentation. Native Shopify values/change events stay authoritative. */
(() => {
  if (window.OeVariantSheet || !window.HTMLDialogElement?.prototype.showModal) return;
  const mobile = window.matchMedia('(max-width: 749px), (hover: none) and (pointer: coarse) and (max-width: 1024px)');
  const controls = new WeakMap();
  // Safari can paint :focus-visible after dialog autofocus and focus return,
  // even when a touch opened it. Track the input method before focus moves.
  document.documentElement.dataset.oeVariantInput = 'pointer';
  document.addEventListener('pointerdown', () => {
    document.documentElement.dataset.oeVariantInput = 'pointer';
  }, { capture: true, passive: true });
  document.addEventListener('keydown', event => {
    if (!event.metaKey && !event.ctrlKey && !event.altKey) {
      document.documentElement.dataset.oeVariantInput = 'keyboard';
    }
  }, true);
  let dialog, current, pendingFocus, scheduled = false, sequence = 0;
  const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const checkIcon = () => {
    const icon = document.createElement('span');
    icon.className = 'oe-variant-sheet__check';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return icon;
  };
  function viewport() {
    if (!current) return;
    const view = window.visualViewport;
    const height = view?.height || window.innerHeight;
    if (current.desktop) {
      const rect = current.trigger.getBoundingClientRect();
      const list = current.content.querySelector('[role=listbox]');
      const chrome = Array.from(current.content.children).filter(e => e !== list).reduce((sum, e) => sum + e.getBoundingClientRect().height, 0);
      const desired = list?.children.length ? Math.min(600, chrome + Math.min(288, list.scrollHeight) + 2) : 432;
      const below = height - rect.top - 12;
      const above = rect.bottom - 12;
      const up = below < desired && above > below;
      dialog.dataset.openingDirection = up ? 'up' : 'down';
      dialog.style.setProperty('--oe-sheet-height', `${Math.max(0, Math.min(desired, up ? above : below))}px`);
      // Document coordinates: page scrolling must never re-pin the popup to
      // the viewport or change its original document position.
      dialog.style.setProperty('--oe-sheet-left', `${rect.left + window.scrollX}px`);
      dialog.style.setProperty('--oe-sheet-width', `${Math.min(rect.width, innerWidth - 24)}px`);
      dialog.style.setProperty('--oe-sheet-top', `${(up ? rect.bottom : rect.top) + window.scrollY}px`);
      dialog.style.setProperty('--oe-sheet-bottom', 'auto');
      dialog.style.setProperty('--oe-sheet-transform', up ? 'translateY(-100%)' : 'none');
      current.anchorTop = rect.top + window.scrollY;
      current.anchorLeft = rect.left + window.scrollX;
      return;
    }
    dialog.style.setProperty('--oe-sheet-height', `${Math.floor(height < 440 ? Math.max(120, height - 8) : height * 0.5)}px`);
    dialog.style.setProperty('--oe-sheet-bottom', `${Math.max(0, window.innerHeight - height - (view?.offsetTop || 0))}px`);
  }
  function ensureDialog() {
    if (dialog) return;
    dialog = document.createElement('dialog');
    dialog.id = 'OeMobileVariantSheet';
    dialog.className = 'oe-variant-sheet';
    dialog.setAttribute('aria-modal', 'true');
    dialog.addEventListener('cancel', event => { event.preventDefault(); close(true); });
    dialog.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); close(true); } });
    // A drag ending outside the panel must not count as a backdrop tap.
    let backdropDown = false;
    const outside = event => {
      const rect = dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    };
    dialog.addEventListener('pointerdown', event => { backdropDown = event.target === dialog && outside(event); });
    dialog.addEventListener('click', event => { if (backdropDown && event.target === dialog && outside(event)) close(true); backdropDown = false; });
    dialog.addEventListener('close', () => { if (current) close(false); });
    document.body.append(dialog);
  }
  function open({ content, trigger, label, initialFocus, onClose, allowDesktop = false }) {
    if (!mobile.matches && !allowDesktop) return false;
    close(false);
    ensureDialog();
    const placeholder = document.createComment('product variant panel');
    content.before(placeholder);
    const bodyStyle = document.body.style;
    const saved = Object.fromEntries(['position', 'top', 'left', 'width', 'overflow'].map(name => [name, bodyStyle[name]]));
    const root = document.documentElement;
    current = { content, trigger, placeholder, onClose, saved, desktop: !mobile.matches, rootOverflow: root.style.overflow, rootOverscroll: root.style.overscrollBehavior, scrollX: window.scrollX, scrollY: window.scrollY, hidden: content.hidden };
    content.hidden = false;
    // Only the rich color renderer receives CARREZ's three-column row styles.
    dialog.classList.toggle('oe-carrez-color', content.matches('[data-color-popup]'));
    dialog.classList.toggle('oe-variant-sheet--desktop', current.desktop);
    dialog.append(content);
    dialog.setAttribute('aria-label', label);
    trigger.setAttribute('aria-expanded', 'true');
    if (current.desktop) trigger.classList.add('oe-variant-trigger--covered');
    dialog.setAttribute('aria-modal', String(!current.desktop));
    if (current.desktop) {
      // Nonmodal dialog.show() focuses its first control. Place it at the field
      // first so the browser cannot scroll to stale coordinates on reopening.
      viewport();
      dialog.show();
    }
    else {
      dialog.showModal();
      Object.assign(bodyStyle, { position: 'fixed', top: `${-current.scrollY}px`, left: `${-current.scrollX}px`, width: '100%', overflow: 'hidden' });
      root.style.overflow = 'hidden'; root.style.overscrollBehavior = 'none';
    }
    viewport();
    // Both renderers populate their list synchronously after open() returns.
    // Measure that finished list before the browser's first paint.
    queueMicrotask(() => { if (current?.trigger === trigger) viewport(); });
    // Focusing the search here opens the iOS keyboard and moves the viewport.
    (initialFocus || content.querySelector('button') || dialog).focus({ preventScroll: true });
    return true;
  }
  function close(restoreFocus = true) {
    if (!current) return;
    const closing = current;
    current = null;
    const active = document.activeElement;
    if (active && dialog.contains(active)) active.blur();
    if (dialog.open) dialog.close();
    closing.content.hidden = closing.hidden;
    if (closing.placeholder.isConnected) closing.placeholder.replaceWith(closing.content);
    else closing.content.remove();
    if (!closing.desktop) {
      const root = document.documentElement;
      root.style.overflow = closing.rootOverflow; root.style.overscrollBehavior = closing.rootOverscroll;
      Object.assign(document.body.style, closing.saved);
      const behavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(closing.scrollX, closing.scrollY);
      root.style.scrollBehavior = behavior;
    }
    closing.trigger.setAttribute('aria-expanded', 'false');
    closing.trigger.classList.remove('oe-variant-trigger--covered');
    closing.onClose?.();
    if (restoreFocus && closing.trigger.isConnected) closing.trigger.focus({ preventScroll: true });
  }
  function selectedText(select) { return select.selectedOptions[0]?.textContent.trim() || ''; }
  function titleFor(select) {
    return select.labels?.[0]?.textContent.trim() || select.name.replace(/^options\[|\]$/g, '') || 'Variante';
  }
  function update(select, control) {
    const text = selectedText(select);
    if (control.value.textContent !== text) control.value.textContent = text;
    control.trigger.disabled = select.disabled;
    select.setAttribute('aria-hidden', 'true');
  }
  function openSelect(select, control) {
    if (select.disabled) return;
    const content = document.createElement('div');
    content.className = 'oe-variant-sheet__content';
    const header = document.createElement('div');
    header.className = 'oe-variant-sheet__heading';
    const exit = document.createElement('button');
    exit.type = 'button'; exit.className = 'oe-variant-sheet__close'; exit.setAttribute('aria-label', `${titleFor(select)} schließen`);
    exit.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m6 15 6-6 6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    exit.addEventListener('click', () => close(true));
    header.append(exit); content.append(header);
    const options = Array.from(select.options);
    let search;
    if (options.length > 8) {
      const bar = document.createElement('div'); bar.className = 'oe-variant-sheet__searchbar';
      search = document.createElement('input'); search.type = 'search'; search.autocomplete = 'off';
      search.placeholder = `${titleFor(select)} suchen …`; search.setAttribute('aria-label', search.placeholder);
      bar.append(search); content.append(bar);
    }
    const list = document.createElement('div');
    list.className = 'oe-variant-sheet__list'; list.setAttribute('role', 'listbox'); list.setAttribute('aria-label', titleFor(select));
    list.tabIndex = 0; content.append(list);
    const status = document.createElement('p'); status.className = 'oe-variant-sheet__status'; status.setAttribute('role', 'status');
    if (search) content.append(status);
    let rows = [], active = 0;
    const highlight = () => {
      rows.forEach((row, index) => { row.toggleAttribute('data-active', index === active); });
      const row = rows[active]; if (!row) return;
      list.setAttribute('aria-activedescendant', row.id);
      const top = row.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop - list.clientTop;
      if (top < list.scrollTop) list.scrollTop = top;
      else if (top + row.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = top + row.offsetHeight - list.clientHeight;
    };
    const choose = index => {
      const option = options[index];
      if (!option || option.disabled || option.parentElement.disabled) return;
      const changed = select.selectedIndex !== index;
      close(true);
      if (!changed || !select.isConnected) return;
      select.selectedIndex = index;
      update(select, control);
      pendingFocus = { id: select.id, select, trigger: control.trigger, until: Date.now() + 5000 };
      // Shopify reads the real option ID/product URL from this native SELECT.
      select.dispatchEvent(new Event('change', { bubbles: true }));
    };
    const render = () => {
      list.replaceChildren(); rows = [];
      options.forEach((option, index) => {
        if (option.hidden || (search && !normalize(option.textContent).includes(normalize(search.value)))) return;
        const row = document.createElement('button'); row.type = 'button'; row.tabIndex = -1;
        row.className = 'oe-variant-sheet__option'; row.id = `${control.trigger.id}-choice-${index}`;
        row.setAttribute('role', 'option'); row.setAttribute('aria-selected', String(option.selected));
        row.disabled = option.disabled || !!option.parentElement.disabled;
        if (row.disabled) row.setAttribute('aria-disabled', 'true');
        if (option.dataset.optionSwatchValue) {
          const swatch = document.createElement('span'); swatch.className = 'oe-variant-sheet__swatch'; swatch.setAttribute('aria-hidden', 'true');
          swatch.style.background = option.dataset.optionSwatchValue; row.classList.add('oe-variant-sheet__option--swatch'); row.append(swatch);
        }
        const text = document.createElement('span'); text.className = 'oe-variant-sheet__label'; text.textContent = option.textContent.trim(); row.append(text);
        if (option.selected) row.append(checkIcon());
        row.addEventListener('click', () => choose(index)); rows.push(row); list.append(row);
      });
      active = Math.max(0, rows.findIndex(row => row.getAttribute('aria-selected') === 'true'));
      status.textContent = rows.length ? `${rows.length} Optionen gefunden` : 'Keine passende Option gefunden';
      highlight();
    };
    const key = event => {
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter'].includes(event.key)) return;
      if (search === event.target && ['Home', 'End'].includes(event.key) && !event.ctrlKey) return;
      event.preventDefault();
      if (event.key === 'Enter') { rows[active]?.click(); return; }
      active = event.key === 'Home' ? 0 : event.key === 'End' ? rows.length - 1 : Math.max(0, Math.min(rows.length - 1, active + (event.key === 'ArrowDown' ? 1 : -1)));
      highlight();
    };
    list.addEventListener('keydown', key); search?.addEventListener('keydown', key); search?.addEventListener('input', render);
    // Detached staging location permits the same move/restore lifecycle as CARREZ.
    control.trigger.after(content);
    open({ content, trigger: control.trigger, label: titleFor(select), initialFocus: list, allowDesktop: true, onClose: () => content.remove() });
    render();
  }
  function refresh() {
    scheduled = false;
    if (current && !current.trigger.isConnected) close(false);
    document.querySelectorAll('variant-selects:not([data-oe-variant-ui-excluded]) select:not([data-color-native]):not([multiple])').forEach(select => {
      let control = controls.get(select);
      if (!control) {
        if (!select.id) select.id = `OeVariantNative-${++sequence}`;
        const trigger = document.createElement('button'); trigger.type = 'button';
        trigger.id = `${select.id}-mobile`; trigger.className = 'oe-mobile-variant-trigger';
        trigger.setAttribute('aria-haspopup', 'dialog'); trigger.setAttribute('aria-controls', 'OeMobileVariantSheet'); trigger.setAttribute('aria-expanded', 'false');
        const value = document.createElement('span'); value.id = `${trigger.id}-value`; trigger.append(value);
        const label = select.labels?.[0];
        if (label) { if (!label.id) label.id = `${trigger.id}-label`; trigger.setAttribute('aria-labelledby', `${label.id} ${value.id}`); }
        else trigger.setAttribute('aria-label', titleFor(select));
        control = { trigger, value }; controls.set(select, control);
        select.setAttribute('data-oe-mobile-variant-native', ''); select.after(trigger);
        trigger.addEventListener('click', () => openSelect(select, control));
        select.addEventListener('change', () => update(select, control));
      }
      update(select, control);
      if (pendingFocus?.id === select.id && select !== pendingFocus.select && Date.now() < pendingFocus.until) {
        if (document.activeElement === document.body || document.activeElement === select) control.trigger.focus({ preventScroll: true });
        pendingFocus = null;
      }
    });
  }
  const observer = new MutationObserver(records => {
    if (scheduled) return;
    if ((current && !current.trigger.isConnected) || records.some(record => record.target.closest?.('variant-selects') || Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.matches('variant-selects, product-info') || node.querySelector('variant-selects'))))) {
      scheduled = true; queueMicrotask(refresh);
    }
  });
  window.OeVariantSheet = { open, close, refresh, get isMobile() { return mobile.matches; } };
  mobile.addEventListener('change', () => { close(true); refresh(); });
  window.visualViewport?.addEventListener('resize', viewport);
  window.visualViewport?.addEventListener('scroll', () => { if (current && !current.desktop) viewport(); });
  window.addEventListener('resize', viewport);
  window.addEventListener('scroll', event => {
    if (!current?.desktop || dialog.contains(event.target)) return;
    const rect = current.trigger.getBoundingClientRect();
    if (rect.bottom <= 0 || rect.top >= innerHeight || rect.right <= 0 || rect.left >= innerWidth ||
      Math.abs(rect.top + scrollY - current.anchorTop) > 2 || Math.abs(rect.left + scrollX - current.anchorLeft) > 2) close(false);
  }, { passive: true, capture: true });
  document.addEventListener('pointerdown', event => {
    if (current?.desktop && !dialog.contains(event.target) && !current.trigger.contains(event.target)) close(false);
  });
  document.addEventListener('focusin', event => {
    if (current?.desktop && !dialog.contains(event.target) && !current.trigger.contains(event.target)) close(false);
  });
  window.addEventListener('pagehide', () => close(false));
  const start = () => { refresh(); observer.observe(document.body, { childList: true, subtree: true }); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
